import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import { getEventCategories } from "../../Requests/EventCategoriesService.ts";
import { EventCategoriesModel } from "../../Models/EventCategoriesModel.ts";
import { Box, Button, Typography } from "@mui/material";
import { Add, FileDownload } from "@mui/icons-material";
import { exportTableToCsv } from "../../Utils/exportTableToCsv.js";
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { EventsCategoriesStoreContext } from "../../Store/EventCategoriesStore.ts";
import AddModalView from "../AddModalView/AddModalView";
import AddEventCategory from "./AddEventCategory.tsx";
import { useNavigate } from "react-router-dom";
import { useRestoreScrollPosition } from "../../Hooks/useScrollRestoration";
import { useTableState } from "../../Hooks/useTableState";
import { useFilteredBySearch } from "../../Hooks/useFilteredBySearch";
import TableSearchWithField from "../TableSearchWithField/TableSearchWithField.jsx";


const COLUMNS = [
  {
    accessorKey: 'name',
    header: 'Category'
  }
]


export default function EventCategoriesView() {

  const navigate = useNavigate()
  const store = useContext(EventsCategoriesStoreContext)
  const tableContainerRef = useRef<HTMLDivElement | null>(null)

  const [globalFilter, setGlobalFilter] = useTableState(store, 'globalFilter', undefined)
  const SEARCH_FIELD_OPTIONS = [
    { value: 'name', label: 'Category' }
  ]
  const [searchField, setSearchField] = useTableState(store, 'searchField', SEARCH_FIELD_OPTIONS[0].value)

  // Use store for state preservation
  const [open, setOpen] = useState<boolean>(store.open)
  useEffect(() => {
    store.open = open
  }, [open, store])

  const [isLoading, setIsloading] = useState<boolean>(store.isLoading)
  useEffect(() => {
    store.isLoading = isLoading
  }, [isLoading, store])

  const [isRefetching, setIsRefetching] = useState<boolean>(store.isRefetching)
  useEffect(() => {
    store.isRefetching = isRefetching
  }, [isRefetching, store])

  const [tableData, setTableData] = useState<EventCategoriesModel[]>(store.tableData)
  useEffect(() => {
    store.tableData = tableData
  }, [tableData, store])

  const fetchEventCategories = async (): Promise<void> => {
    const result = await getEventCategories()
    setIsloading(false)
    setIsRefetching(false)
    setTableData(result)
  }

  useEffect(() => {
    // Restore state from store if data exists
    if (store.tableData.length > 0 && !store.needsRefresh) {
      setTableData(store.tableData)
      setIsloading(false)
      setIsRefetching(false)
    } else {
      // Fetch data if not loaded or refresh is needed
      if (store.needsRefresh) {
        store.needsRefresh = false
      }
      fetchEventCategories()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Restore scroll position after table is rendered and data is loaded
  useRestoreScrollPosition(tableContainerRef, store, tableData, isLoading)

  const columns = useMemo<MRT_ColumnDef<EventCategoriesModel>[]>(() => COLUMNS, [])
  const data = useFilteredBySearch(tableData, globalFilter, searchField)

  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnFilters: false,
    enableHiding: false,
    enableGlobalFilter: false,
    state: {
      isLoading: isLoading,
      showProgressBars: isRefetching,
      globalFilter: globalFilter,
      showGlobalFilter: false
    },
    onGlobalFilterChange: setGlobalFilter,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        // Save scroll position before navigation
        if (tableContainerRef.current) {
          store.scrollTop = tableContainerRef.current.scrollTop
        }
        navigate(`/eventcategory/${row.original.objectId}`)
      },
      sx: {
        cursor: 'pointer'
      }
    }),
    renderTopToolbarCustomActions: ({ table }) => {
      const handleExport = () => {
        const done = exportTableToCsv(table, "event-categories");
        if (!done) alert("No data to export.");
      };
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', p: '4px', flexWrap: 'wrap', width: '100%' }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <TableSearchWithField
                options={SEARCH_FIELD_OPTIONS}
                searchField={searchField}
                onSearchFieldChange={setSearchField}
                globalFilter={globalFilter}
                onGlobalFilterChange={setGlobalFilter}
                selectMinWidth={160}
              />
            </Box>
            <Button
              size="small"
              startIcon={<FileDownload />}
              onClick={handleExport}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => setOpen(true)}
            >
              <Add />
              Add Category
            </Button>
          </Box>
        </Box>
      );
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      },
      onScroll: (e) => {
        // Save scroll position while scrolling
        if (e.target instanceof HTMLElement) {
          store.scrollTop = e.target.scrollTop
        }
      }
    }
  })

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        paddingTop={2}
        paddingBottom={1}
      >
        <Typography
          variant="h4"
        >
          Event Categories
        </Typography>
      </Box>

      <MaterialReactTable
        table={table}
      />

      <AddModalView
        isOpen={open}
        setIsOpen={setOpen}
        title={"Add Category"}
        view={
          <AddEventCategory publish={() => {
            setOpen(false)
            setIsRefetching(true)
            fetchEventCategories()
          }} />}
      />
    </>
  )
}
